/**
 * CategoryPicker — controlled radio-grid used inside the voucher composer.
 *
 * Thin wrapper over `<QuickAddGrid mode="select" />`: the parent owns the
 * `value` state (usually RHF), and we forward `onChange` to the grid's
 * `onSelect`. The composer needs a "this-month" total per tile to
 * surface spend signals while picking — those come from
 * `pettyCash.anchorData.thisMonth.perCategory`, mapped to the
 * `QuickAddGridCategory[]` shape by the route.
 *
 * Why a wrapper at all? It guarantees the composer's category state can
 * never reach an invalid slug (the `PettyCashCategory` union is the only
 * type the value/onChange accept), and it keeps the composer file thin.
 */
import type { PettyCashCategory } from "../lib/categories.tsx";
import { QuickAddGrid, type QuickAddGridCategory } from "./quick-add-grid.tsx";

export interface CategoryPickerProps {
  /** Eight rows expected, one per category. */
  categories: readonly QuickAddGridCategory[];
  /** The currently-selected slug, or `undefined` when nothing is picked yet. */
  value: PettyCashCategory | undefined;
  onChange: (slug: PettyCashCategory) => void;
}

export function CategoryPicker({ categories, value, onChange }: CategoryPickerProps) {
  return (
    <QuickAddGrid
      mode="select"
      categories={categories}
      // Conditional spread keeps `selected` absent (not `undefined`) when
      // unset — required by `exactOptionalPropertyTypes`.
      {...(value !== undefined ? { selected: value } : {})}
      onSelect={onChange}
    />
  );
}
